import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tabs,
  Tab,
  Alert,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import ReactMarkdown from 'react-markdown';

const BulkQueryResults = ({ results }) => {
  const [selectedQuery, setSelectedQuery] = useState(null);
  
  if (!results) {
    return null;
  }
  
  const { results: queryResults, rag_strategy_used } = results;
  
  const handleExportResults = () => {
    // Create CSV content
    let csvContent = 'ID,Question,Answer\n';
    
    queryResults.forEach(result => {
      // Escape quotes in the answer and question
      const escapedQuestion = result.question.replace(/"/g, '""');
      const escapedAnswer = result.answer.replace(/"/g, '""');
      
      csvContent += `"${result.id}","${escapedQuestion}","${escapedAnswer}"\n`;
    });
    
    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `bulk_query_results_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Bulk Query Results
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportResults}
        >
          Export CSV
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />
      
      <Box sx={{ mb: 2 }}>
        <Chip 
          label={`Strategy: ${rag_strategy_used}`} 
          color="primary" 
          sx={{ mr: 1 }}
        />
        <Chip 
          label={`Total Queries: ${queryResults.length}`} 
          color="secondary" 
        />
      </Box>
      
      {queryResults.map((result) => (
        <Accordion 
          key={result.id}
          sx={{ mb: 1 }}
          expanded={selectedQuery === result.id}
          onChange={() => setSelectedQuery(selectedQuery === result.id ? null : result.id)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 'bold' }}>
                {result.question}
              </Typography>
              {result.error && (
                <Chip 
                  label="Error" 
                  color="error" 
                  size="small" 
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {result.error ? (
              <Alert severity="error">
                {result.error}
              </Alert>
            ) : (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Answer
                </Typography>
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                  <ReactMarkdown>{result.answer}</ReactMarkdown>
                </Box>
                
                <Typography variant="h6" gutterBottom>
                  Retrieved Chunks
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {result.retrieved_chunks.map((chunk, index) => (
                    <Accordion key={chunk.id} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                          <Typography>
                            Chunk {index + 1}: {chunk.metadata.document_id || 'Unknown Document'}
                          </Typography>
                          <Chip 
                            label={`Score: ${chunk.score ? chunk.score.toFixed(2) : 'N/A'}`} 
                            color={chunk.score > 0.7 ? 'success' : chunk.score > 0.5 ? 'primary' : 'default'}
                            size="small"
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {chunk.text_chunk}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
                
                {result.reasoning_log && result.reasoning_log.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Reasoning Process
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {result.reasoning_log[result.reasoning_log.length - 1].final_note || 
                       'Reasoning process completed successfully.'}
                    </Alert>
                  </Box>
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
};

export default BulkQueryResults;
